import { TOURNAMENT_FORMATS } from '../types/tournamentTypes';
import { buildBracket, finalizeByeMatches, finalizeSingleByeMatch, undoSingleByeMatch, propagateAutoWinners, getStandings } from './doubleEliminationUtils';
import { buildSingleEliminationBracket, finalizeSingleEliminationByeMatches, propagateSingleEliminationWinners, getSingleEliminationStandings } from './singleEliminationUtils';
import { buildModifiedSingleEliminationBracket, finalizeModifiedSingleEliminationByeMatches, propagateModifiedSingleEliminationWinners, getModifiedSingleEliminationStandings } from './modifiedSingleEliminationUtils';
import { buildRoundRobinBracket, finalizeRoundRobinByeMatches, propagateRoundRobinWinners, getRoundRobinStandings } from './roundRobinUtils';
import { stripSourceMatch } from './bracketSchema';

// Tournament configuration map
const TOURNAMENT_CONFIG = {
  [TOURNAMENT_FORMATS.DOUBLE_ELIMINATION]: {
    buildBracket: buildBracket,
    finalizeByeMatches: finalizeByeMatches,
    finalizeSingleByeMatch: finalizeSingleByeMatch,
    undoSingleByeMatch: undoSingleByeMatch,
    propagateWinners: propagateAutoWinners,
    getStandings: getStandings,
    hasLosers: true,
    hasTableView: true,
    hasBracketView: true,
    navigationButtons: ['winners', 'losers']
  },
  [TOURNAMENT_FORMATS.SINGLE_ELIMINATION]: {
    buildBracket: buildSingleEliminationBracket,
    finalizeByeMatches: finalizeSingleEliminationByeMatches,
    propagateWinners: propagateSingleEliminationWinners,
    getStandings: getSingleEliminationStandings,
    hasLosers: false,
    hasTableView: true,
    hasBracketView: true,
    navigationButtons: [] // No navigation buttons needed for single elimination
  },
  [TOURNAMENT_FORMATS.MODIFIED_SINGLE_ELIMINATION]: {
    buildBracket: buildModifiedSingleEliminationBracket,
    finalizeByeMatches: finalizeModifiedSingleEliminationByeMatches,
    propagateWinners: propagateModifiedSingleEliminationWinners,
    getStandings: getModifiedSingleEliminationStandings,
    hasLosers: false,
    hasTableView: true,
    hasBracketView: true,
    navigationButtons: [] // No navigation buttons needed for MSE
  },
  [TOURNAMENT_FORMATS.ROUND_ROBIN]: {
    buildBracket: buildRoundRobinBracket,
    finalizeByeMatches: finalizeRoundRobinByeMatches,
    propagateWinners: propagateRoundRobinWinners,
    getStandings: getRoundRobinStandings,
    hasLosers: false,
    hasTableView: true,
    hasBracketView: false, // Round Robin doesn't have a bracket view
    navigationButtons: [] // No navigation buttons needed for Round Robin
  }
};

export class TournamentManager {
  constructor(format) {
    this.format = format;
    this.config = TOURNAMENT_CONFIG[format];
    
    if (!this.config) {
      throw new Error(`Unsupported tournament format: ${format}`);
    }
  }

  buildBracket(players) {
    // Strip the recursive `sourceMatch` build artifact before any consumer sees the
    // bracket. It is only needed internally to compute each match's `from` routing
    // (already done by now) and would otherwise nest exponentially in the losers
    // bracket, bloating in-memory state and the persisted Firestore document.
    return stripSourceMatch(this.config.buildBracket(players));
  }

  finalizeByeMatches(bracket, callback) {
    return this.config.finalizeByeMatches(bracket, callback);
  }

  finalizeSingleByeMatch(bracket, match, callback) {
    if (this.config.finalizeSingleByeMatch) {
      return this.config.finalizeSingleByeMatch(bracket, match, callback);
    }
    // Fallback to global finalization if individual not available
    return this.config.finalizeByeMatches(bracket, callback);
  }

  undoSingleByeMatch(bracket, match) {
    if (this.config.undoSingleByeMatch) {
      return this.config.undoSingleByeMatch(bracket, match);
    }
    return false; // Individual undo not supported for this format
  }

  propagateWinners(bracket) {
    return this.config.propagateWinners(bracket);
  }

  getStandings(bracket) {
    return this.config.getStandings(bracket);
  }

  hasLosers() {
    return this.config.hasLosers;
  }

  hasTableView() {
    return this.config.hasTableView;
  }

  hasBracketView() {
    return this.config.hasBracketView;
  }

  getNavigationButtons() {
    return this.config.navigationButtons;
  }

  // Utility method to check if a bracket belongs to this format
  isMatchingBracket(bracket) {
    if (this.format === TOURNAMENT_FORMATS.SINGLE_ELIMINATION) {
      return bracket.some(m => m.bracket === 'SE');
    } else if (this.format === TOURNAMENT_FORMATS.DOUBLE_ELIMINATION) {
      return bracket.some(m => m.bracket === 'W' || m.bracket === 'L' || m.bracket === 'GF');
    } else if (this.format === TOURNAMENT_FORMATS.MODIFIED_SINGLE_ELIMINATION) {
      return bracket.some(m => m.bracket === 'MSE');
    } else if (this.format === TOURNAMENT_FORMATS.ROUND_ROBIN) {
      return bracket.some(m => m.bracket === 'RR');
    }
    return false;
  }
}

// Factory function to create tournament manager
export function createTournamentManager(format) {
  return new TournamentManager(format);
}

// Utility function to detect tournament format from bracket
export function detectTournamentFormat(bracket) {
  if (bracket.some(m => m.bracket === 'RR')) {
    return TOURNAMENT_FORMATS.ROUND_ROBIN;
  } else if (bracket.some(m => m.bracket === 'MSE')) {
    return TOURNAMENT_FORMATS.MODIFIED_SINGLE_ELIMINATION;
  } else if (bracket.some(m => m.bracket === 'SE')) {
    return TOURNAMENT_FORMATS.SINGLE_ELIMINATION;
  } else if (bracket.some(m => m.bracket === 'W' || m.bracket === 'L' || m.bracket === 'GF')) {
    return TOURNAMENT_FORMATS.DOUBLE_ELIMINATION;
  }
  return null;
}

/**
 * Apply bracket logic with winner propagation until stable
 * This replaces the repeated do-while loop pattern used throughout the codebase
 * @param {Array} bracket - The bracket array to process
 * @param {string} format - The tournament format
 * @param {number} maxPasses - Maximum iterations to prevent infinite loops (default: 10)
 * @returns {Array} - The processed bracket with winners propagated
 */
export function applyBracketLogicWithPropagation(bracket, format, maxPasses = 10) {
  if (!bracket || bracket.length === 0) {
    return bracket;
  }

  const tournamentManager = createTournamentManager(format);
  let processedBracket = JSON.parse(JSON.stringify(bracket));
  let prevBracketState;
  let passes = 0;

  do {
    prevBracketState = JSON.stringify(processedBracket);
    tournamentManager.finalizeByeMatches(processedBracket);
    tournamentManager.propagateWinners(processedBracket);
    passes++;
  } while (JSON.stringify(processedBracket) !== prevBracketState && passes < maxPasses);

  return processedBracket;
}

/**
 * Extract user ID from playerUserIds data structure
 * Handles both simple string userId and object format (for doubles)
 * @param {string|object} userIdData - The userId data from playerUserIds
 * @returns {string|null} - The extracted user ID or null
 */
export function extractUserIdFromPlayerUserIds(userIdData) {
  if (!userIdData) return null;
  return typeof userIdData === 'string' ? userIdData : userIdData?.odUserId || null;
}

/**
 * Build a player to userId mapping from tournament playerUserIds
 * @param {object} playerUserIds - The playerUserIds object from tournament
 * @returns {Map} - Map of playerName -> { odUserId, displayName, player1UserId?, player2UserId? }
 */
export function buildPlayerUserIdMap(playerUserIds) {
  const mapping = new Map();

  if (!playerUserIds) return mapping;

  Object.entries(playerUserIds).forEach(([playerName, userIdData]) => {
    const odUserId = extractUserIdFromPlayerUserIds(userIdData);
    if (odUserId) {
      mapping.set(playerName, {
        odUserId: odUserId,
        displayName: playerName,
        // For doubles, store individual player IDs
        player1UserId: typeof userIdData === 'object' ? userIdData.player1UserId : undefined,
        player2UserId: typeof userIdData === 'object' ? userIdData.player2UserId : undefined
      });
    }
  });

  return mapping;
}